import React, { useState, useEffect, useRef } from "react";
import pageTransition from "../components/pageTransition";
import CategoryCard from "../components/categoryCard";
import StartSettings from "../components/startSettings";
import SearchBar from "../components/searchBar";
import ConsentPopover from '../components/consentPopover';
import { useTranslation } from "react-i18next";
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MetaHelmet from '../components/metaHelmet';
import { getCategoryBySlug, getParentSlugOfId } from '../helper/Data';
import { getCurrentWeekSlug } from '../assets/categorys_of_the_week';

const Home: React.FC = () => {
    const { t } = useTranslation();
    // Load settings
    const [settingsSet] = useState<boolean>(() => !!localStorage.getItem('settings-set'));
    const [language] = useState(localStorage.getItem('i18nextLng') || 'en');

    const location = useLocation();
    const internalOrigin = location.state?.fromInternal || false;
    const { trackPageView } = useMatomo();

    const navigate = useNavigate();

    const showNewToast = async () => {
        // Create the toast element
        const toast = document.createElement('ion-toast');
        toast.message = 'Willkommen bei smartGastro. Kennst du schon unsere Funktionen?';
        toast.duration = 10000; // Show for 10
        toast.position = 'bottom'; // Or 'bottom', 'middle'
        // Add buttons to the toast
        toast.mode = 'ios';
        toast.swipeGesture = 'vertical';
        toast.buttons = [
            {
                text: 'Mehr dazu',
                role: 'info',
                handler: () => {
                    navigate(`/funktionen`, { state: { fromInternal: true } });
                },
            },
        ];
        toast.cssClass = 'info-toast';

        // Append the toast to the document body
        document.body.appendChild(toast);

        // Present the toast
        await toast.present();
    };

    // Function to parse custom date format with seconds
    const parseCustomDate = (dateString: string): Date => {
        const [day, month, yearAndTime] = dateString.split('.');
        const [year, time] = yearAndTime.split(', ');
        const [hours, minutes, seconds] = time.split(':');

        return new Date(
            parseInt(year),        // Year
            parseInt(month) - 1,   // Month (0-indexed in JavaScript Date)
            parseInt(day),         // Day
            parseInt(hours),       // Hours
            parseInt(minutes),     // Minutes
            parseInt(seconds)      // Seconds
        );
    };

    const checkConsentAndShowToast = () => {
        const i18nextLang = localStorage.getItem('i18nextLng');
        const consentGiven = localStorage.getItem('consent-given');

        // Combine the checks for language and consent time
        if (i18nextLang === 'de' && consentGiven) {
            const consentTime = parseCustomDate(consentGiven);
            const currentTime = new Date().getTime();
            const timeDifference = (currentTime - consentTime.getTime()) / 1000; // time in seconds

            // Show toast if the consent was given less than 4 seconds ago
            if (timeDifference < 4) {
                showNewToast();
            }
        }
    };


    useEffect(() => {
        trackPageView({
            href: window.location.href, // URL der aktuellen Seite
        });

        checkConsentAndShowToast();
    }, [location]);

    return (
        !settingsSet ? (
            <ion-page class="sg-body">
                <StartSettings />
            </ion-page>
        ) : (
            <ion-page class="sg-body home">
                {
                    //<ConsentPopover />
                }
                <div className="page-container">
                    <MetaHelmet
                        title=""
                    />
                    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                        <img className="post-image" src={'/images/home_page_new.jpeg'} />
                        <div className="home-logo-circle" />
                        <img 
                            src={'/icons/smartGastro-rund-white.svg'} 
                            className="home-logo"
                        />
                    </div>
                    <div className="header-container ion-padding home">
                        <h1 className="start-title">
                            {t('start_title')}
                        </h1>
                    </div>
                    <div className="searchbar-container">
                        <SearchBar initialtext='' />
                    </div>
                    <div className="grid-container home">
                        <CategoryCard currentUrl={""} name={t('essen')} slug="essen" isParent={true} />
                        <CategoryCard currentUrl={""} name={t('trinken')} slug="trinken" isParent={true} />
                        <CategoryCard currentUrl={""} name={t('aktivitaeten')} slug="aktivitaeten" isParent={true} />
                    </div>
                    <div className="newest-post home-section">
                        <h2 className="subtitle">{t('newest_post')}</h2>
                        {(() => {
                            const tempPosts = localStorage.getItem('news');
                            if (!tempPosts) return null;
                            const posts = JSON.parse(tempPosts);
                            if (!posts || posts.length === 0) return null;
                            const newest = posts[0];
                            const plainText = newest.content?.replace(/<[^>]+>/g, '') || '';
                            const firstLines = plainText.split('\n').filter((l: string) => l.trim()).slice(0, 3).join(' ');
                            const preview = firstLines.length > 100 ? firstLines.substring(0, 100) + '...' : firstLines + '...';
                            return (
                                <>
                                    <p className="text">{preview}</p>
                                    <Link state={{ fromInternal: true }} className="search-results-item" to={`/aktuelles/${newest.slug}`}>
                                        <span className="search-results-item-category news-link" style={{ backgroundImage: `url("/icons/news-green.svg")` }}>
                                            {newest.title}
                                        </span>
                                    </Link>
                                </>
                            );
                        })()}
                    </div>
                    <div className="cow home-section">
                        <h2 className="subtitle">{t('category_of_the_week')}</h2>
                        {(() => {
                            const category = getCategoryBySlug(getCurrentWeekSlug());
                            if (!category) return null;
                            const parentSlug = getParentSlugOfId(category.parent_id);
                            return (
                                <>
                                    <p className="text" dangerouslySetInnerHTML={{ __html: language === 'de' ? category.description_de : category.description_en }}>
                                    </p>
                                    <Link state={{ fromInternal: true }} className="search-results-item" to={`/${parentSlug}/${category.slug}`}>
                                        <span className="search-results-item-category news-link" style={{ backgroundImage: `url("/icons/${category.slug}.svg")` }}>
                                            {language === 'de' ? category.name_de : category.name_en}
                                        </span>
                                    </Link>
                                </>
                            );
                        })()}
                    </div>
                    <div className="social-media-links">
                    <a href="https://www.facebook.com/smartGastroAT/" target="_blank" className="contact-link social-media-links__item"
                    style={{ backgroundImage: `url("/icons/facebook-blue.svg")` }}></a>
                    <a href="https://www.instagram.com/smartgastroat/" target="_blank" className="contact-link social-media-links__item"
                    style={{ backgroundImage: `url("/icons/instagram_gradient.png")` }}></a>
                    <a href="https://www.tiktok.com/@smartgastroat" target="_blank" className="contact-link social-media-links__item"
                    style={{ backgroundImage: `url("/icons/TikTok_Icon_Black_Circle.png")` }}></a>
                    </div>
                </div>
            </ion-page>
        )
    );
};

export default pageTransition(Home);
